import { useEffect, useState } from 'react';

import { Box, Button, ButtonGroup, FormHelp, Heading, Legend, Modal, TextButton } from '@zillow/constellation-10';

import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import { PaymentAuthorizationTerms } from '@common/components/PaymentAuthorizationTerms/PaymentAuthorizationTerms';
import type { RenterMoveInCostsDetailsInterface, RenterPaymentSubscriptionInterface } from '@renter-hub/models/Types';
import { MOVE_IN_COSTS_PAYMENT, RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';

interface Props {
  onBack: () => void;
  paymentSubscriptionDetails: RenterPaymentSubscriptionInterface;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  typeOfPaymentToMake?: typeof RENT_PAYMENT | typeof MOVE_IN_COSTS_PAYMENT;
  updatePaymentMethod: (paymentMethodId: string) => void;
  onAddNewPaymentClick: () => void;
  newPaymentMethodId: string;
}
const UpdatePaymentMethodsPage = ({
  paymentSubscriptionDetails,
  moveInCostsDetails,
  onBack,
  typeOfPaymentToMake = RENT_PAYMENT,
  updatePaymentMethod,
  onAddNewPaymentClick,
  newPaymentMethodId,
}: Props) => {
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (newPaymentMethodId) {
      setSelectedPaymentMethodId(newPaymentMethodId);
    } else if (typeOfPaymentToMake === MOVE_IN_COSTS_PAYMENT) {
      setSelectedPaymentMethodId(moveInCostsDetails.paymentMethodId || '');
    } else {
      setSelectedPaymentMethodId(paymentSubscriptionDetails.paymentMethodId || '');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const isRecurringRent = typeOfPaymentToMake === RENT_PAYMENT && paymentSubscriptionDetails.recurring;

  const getPaymentDateText = () => {
    const isRent = typeOfPaymentToMake === RENT_PAYMENT;
    const date = isRent ? paymentSubscriptionDetails.nextScheduledDate : moveInCostsDetails.sendDate;
    const amount = isRent ? paymentSubscriptionDetails.amount : moveInCostsDetails.totalAmount;
    const subject = amount == null ? 'Your payment' : centsToDollars(amount);

    return isRecurringRent
      ? `${subject} will be processed on the ${formatDate(date, 'do')} of each month`
      : `${subject} will be processed on ${formatDate(date, 'MMMM d, yyyy')}`;
  };

  const isButtonDisabled = () => {
    const paymentMethodIdToCheck =
      typeOfPaymentToMake === MOVE_IN_COSTS_PAYMENT
        ? moveInCostsDetails.paymentMethodId
        : paymentSubscriptionDetails.paymentMethodId;

    return selectedPaymentMethodId === paymentMethodIdToCheck;
  };
  return (
    <Modal
      open={true}
      trigger={null}
      size="md"
      header={<Heading level={2}>Select a payment method</Heading>}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onBack();
        }
      }}
      body={
        <>
          <AvailablePaymentMethods
            selectedPaymentMethodId={selectedPaymentMethodId}
            onPaymentMethodClick={(paymentMethod) => setSelectedPaymentMethodId(paymentMethod.id)}
            renderRadioView={true}
            legend={<Legend css={{ textStyle: 'body-bold' }}>Payment method</Legend>}
            description={<FormHelp>{getPaymentDateText()}</FormHelp>}
          />
          <Box css={{ marginTop: 'default' }}>
            <PaymentAuthorizationTerms variant={isRecurringRent ? 'recurring' : 'oneTime'} ctaLabel="Update" />
          </Box>
        </>
      }
      footer={
        <ButtonGroup aria-label="Modal actions">
          <TextButton textStyle="body-bold" onClick={onAddNewPaymentClick} css={{ alignItems: 'center' }}>
            Add new payment method
          </TextButton>
          <Button
            disabled={isButtonDisabled()}
            emphasis="filled"
            onClick={async () => {
              if (selectedPaymentMethodId) {
                setIsLoading(true);
                await updatePaymentMethod(selectedPaymentMethodId);
                setIsLoading(false);
              }
            }}
            loading={isLoading}
          >
            Update
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default UpdatePaymentMethodsPage;
